// client/src/components/bank/VatCreditOpenRequests.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatCurrencyWithSymbol } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const VatCreditOpenRequests = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Le richieste sono già filtrate lato server per la banca corrente
        const openRequestsRes = await axios.get('/api/vat-credit-requests');

        // Fetch delle offerte della banca per capire su quali richieste ha già offerto
        const userBidsRes = await axios.get('/api/vat-credit-bids/bank');

        // Filter out bids with null requests
        const validBids = (userBidsRes.data.bids || []).filter(bid => bid.request !== null);

        // Filter out requests that the user has already bid on with ACTIVE bids
        const requestsWithoutActiveBids = (openRequestsRes.data.requests || []).filter(request => {
          const hasActiveBidForRequest = validBids.some(bid =>
            bid.request?.requestId === request.requestId &&
            bid.status !== 'cancelled' &&
            bid.status !== 'rejected'
          );

          return !hasActiveBidForRequest;
        });

        setRequests(requestsWithoutActiveBids);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching VAT credit open requests:', err);
        setError(t('common.errors.fetchRequests') + ': ' + (err.response?.data?.message || err.message));
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const openBidForm = (requestId) => {
    navigate(`/bank/vat-credit/submit-bid/${requestId}`);
  };

  // Mobile Card Component
  const MobileVatCreditRequestCard = ({ request }) => (
    <div className="mobile-record-card">
      <div className="mobile-card-header">
        <div className="mobile-card-title">
          #{request.requestId}
        </div>
        <div className="mobile-card-status">
          <span className="status status-open">{t('status.open')}</span>
        </div>
      </div>

      <div className="mobile-card-body">
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.company')}</span>
          <span className="mobile-card-value">{request.company?.name || t('bids.notAvailable')}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.amount')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.indicativeDays')}</span>
          <span className="mobile-card-value">{request.indicativeDays} {t('common.days')}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.bidDeadline')}</span>
          <span className="mobile-card-value">
            {new Date(request.bidDeadline).toLocaleDateString()}
            {' '}
            <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
              {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} <span style={{ fontSize: '0.8em' }}>CET</span>
            </span>
          </span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.executionDate')}</span>
          <span className="mobile-card-value">{new Date(request.executionDate).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="mobile-card-date">
          {/* Spazio per eventuali info aggiuntive */}
        </div>
        <div className="mobile-card-action">
          <button
            onClick={() => openBidForm(request.id)}
            className="view-btn"
          >
            {t('bids.submitBid')}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="loading">{t('common.loadingRequests')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="open-requests company-dashboard">
      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title">
          <Plus className="card-title-icon" size={20} />
          <span>{t('vatCredit.openRequests')}</span>
        </div>

        {/* Desktop Table */}
        {!isMobile && (
          <table style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: '13%' }}>{t('requests.requestId')}</th>
                <th style={{ width: '18%' }}>{t('requests.company')}</th>
                <th style={{ width: '16%' }}>{t('vatCredit.amount')}</th>
                <th style={{ width: '11%' }}>{t('vatCredit.indicativeDaysShort')}</th>
                <th style={{ width: '17%' }}>{t('vatCredit.bidDeadline')}</th>
                <th style={{ width: '13%' }}>{t('vatCredit.executionDate')}</th>
                <th style={{ width: '10%' }}>{t('bids.status')}</th>
                <th style={{ width: '12%' }}>{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {requests.length > 0 ? (
                requests.map(request => (
                  <tr key={request.id}>
                    <td>#{request.requestId}</td>
                    <td>{request.company?.name || t('bids.notAvailable')}</td>
                    <td>{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</td>
                    <td>{request.indicativeDays}</td>
                    <td>
                      {new Date(request.bidDeadline).toLocaleDateString()}
                      {' '}
                      <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
                        {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} <span style={{ fontSize: '0.8em' }}>CET</span>
                      </span>
                    </td>
                    <td>{new Date(request.executionDate).toLocaleDateString()}</td>
                    <td>
                      <span className="status status-open" style={{ fontSize: '10px', padding: '3px 7px', lineHeight: '1.4', border: '1px solid transparent' }}>
                        {t('status.open')}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => openBidForm(request.id)}
                        className="view-btn"
                        style={{
                          padding: '8px 16px',
                          fontSize: '13px'
                        }}
                      >
                        {t('bids.submitBid')}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">{t('requests.noNewRequests')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* Mobile Cards - Solo su mobile */}
        {isMobile && (
          <div className="mobile-cards-container">
            {requests.length > 0 ? (
              requests.map(request => (
                <MobileVatCreditRequestCard key={request.id} request={request} />
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">
                {t('requests.noNewRequests')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VatCreditOpenRequests;
